import { JsonDetails } from "@superadmin/components/JsonDetails";
import { PageShell } from "@superadmin/components/PageShell";
import { orpc } from "@superadmin/utils/orpc.tanstack.client";
import { useQuery } from "@tanstack/react-query";
import { LoadingSpinner } from "@voicely/ui-mui/components/LoadingSpinner";
import { Typography } from "@voicely/ui-mui/data-display/Typography";
import { Alert } from "@voicely/ui-mui/feedback/Alert";
import { TextField } from "@voicely/ui-mui/form/TextField";
import { Stack } from "@voicely/ui-mui/layout/Stack";
import { useUrlState } from "@voicely/web-shared/use_url_state";
import { z } from "zod";

/**
 * "Was this agent even configured to understand that caller" — the lookup
 * behind the `agent_config` MCP tool. Takes an agent ULID or a session ULID;
 * the id lives in the URL so a finding can be linked in a bug report.
 */
export const AgentConfigPage = () => {
	const [id, setId] = useUrlState("id", z.string(), "");

	const configQuery = useQuery(
		orpc.superAdmin.agentConfig.queryOptions({
			input: { id },
			enabled: id !== "",
		}),
	);

	const toolbar = (
		<TextField
			size="small"
			label="Agent ULID or session ULID"
			defaultValue={id}
			onBlur={(event) => setId(event.target.value.trim())}
			onKeyDown={(event) => {
				if (event.key === "Enter") setId((event.target as HTMLInputElement).value.trim());
			}}
			sx={{ minWidth: 340 }}
		/>
	);

	return (
		<PageShell
			title="Agent config"
			subtitle="The two language choices, what was derived from them, and whether each vendor binding can serve them"
			toolbar={toolbar}
		>
			<Stack spacing={2}>
				{id === "" && (
					<Typography color="text.secondary">
						Paste an agent id — or a session id from a call dump — and press Enter.
					</Typography>
				)}
				{id !== "" && configQuery.isPending && <LoadingSpinner />}
				{configQuery.error && <Alert severity="error">{configQuery.error.message}</Alert>}
				{configQuery.data !== undefined && (
					<JsonDetails label={`Config for ${id}`} value={configQuery.data} />
				)}
			</Stack>
		</PageShell>
	);
};
